// Copyright (c) Ping. All rights reserved.
//
// Preset virtual backgrounds for in-call effects. Four real photographic
// backgrounds (Office / Forest / Gym / Coast) ship as bundled same-origin assets
// under `/call-backgrounds/` — full-res 1280×720 for the MediaPipe compositor and
// a light 200px thumbnail for the menu tiles. Same-origin keeps the <canvas>/WebGL
// pipeline untainted and the browser HTTP cache means they load once. The chosen
// effect (blur / preset / custom upload) is persisted to localStorage and
// re-applied automatically when the next call connects. Custom uploads are
// downscaled to a JPEG data URL and kept in a local library so users can re-pick
// any image they've added before — data URLs survive reloads and need no
// `revokeObjectURL`, avoiding the object-URL leak the old file-picker path had.

import type { BgEffectKind } from './backgroundController';

export interface PresetBackground {
  /** Stable id persisted across sessions (also the asset filename). */
  readonly id: string;
  /** Tile label (DS "Background effects"). */
  readonly label: string;
}

/** The built-in photographic backgrounds, in display order (Figma 889:15715). */
export const PRESET_BACKGROUNDS: readonly PresetBackground[] = [
  { id: 'office', label: 'Office' },
  { id: 'forest', label: 'Forest' },
  { id: 'gym', label: 'Gym' },
  { id: 'coast', label: 'Coast' },
] as const;

const PRESET_IDS = new Set(PRESET_BACKGROUNDS.map((p) => p.id));
const W = 1280;

/** Full-res asset URL for the MediaPipe compositor (or null for unknown ids). */
export function presetImageUrl(id: string): string | null {
  return PRESET_IDS.has(id) ? `/call-backgrounds/${id}.jpg` : null;
}

/** Light thumbnail URL for the menu tile (falls back to the full image). */
export function presetThumbUrl(id: string): string | null {
  return PRESET_IDS.has(id) ? `/call-backgrounds/thumb/${id}.jpg` : null;
}

/**
 * Downscale an uploaded image File to a JPEG data URL (max 1280px on the long
 * edge). Data URLs persist across reloads and need no object-URL revocation.
 */
export async function fileToBackgroundDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, W / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.86);
  } finally {
    bitmap.close();
  }
}

// ── Custom background library (reusable, cached locally) ─────────────────────

const LS_LIBRARY = 'ping.call.bg.library';
const MAX_CUSTOM = 12;

export interface CustomBackground {
  readonly id: string;
  /** JPEG data URL (downscaled). */
  readonly dataUrl: string;
}

/** Every custom background the user has added, newest first. */
export function listCustomBackgrounds(): CustomBackground[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LS_LIBRARY);
    if (raw === null) return [];
    const list = JSON.parse(raw) as CustomBackground[];
    return Array.isArray(list) ? list.filter((c) => typeof c?.dataUrl === 'string') : [];
  } catch {
    return [];
  }
}

/** Add an uploaded image to the reusable library; returns its stable id. */
export function addCustomBackground(dataUrl: string): string {
  const id = `custom:${dataUrl.length.toString(36)}:${dataUrl.slice(-16).replace(/[^a-z0-9]/gi, '')}`;
  if (typeof localStorage === 'undefined') return id;
  const existing = listCustomBackgrounds();
  // Dedupe by data URL so re-adding the same file reuses its tile.
  const deduped = existing.filter((c) => c.dataUrl !== dataUrl);
  const next = [{ id, dataUrl }, ...deduped].slice(0, MAX_CUSTOM);
  try {
    localStorage.setItem(LS_LIBRARY, JSON.stringify(next));
  } catch {
    /* storage full — newest still returned for this session */
  }
  return id;
}

/** Resolve a custom background id back to its cached data URL. */
export function customBackgroundUrl(id: string): string | null {
  return listCustomBackgrounds().find((c) => c.id === id)?.dataUrl ?? null;
}

/** Remove a custom background from the library. */
export function removeCustomBackground(id: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LS_LIBRARY, JSON.stringify(listCustomBackgrounds().filter((c) => c.id !== id)));
  } catch {
    /* ignore */
  }
}

// ── Selection persistence ───────────────────────────────────────────────────

const LS_KEY = 'ping.call.bg';

export interface PersistedBg {
  readonly kind: BgEffectKind;
  /** Preset id or custom id, when kind === 'image'. */
  readonly imageId: string | null;
}

/** Persist the current selection so the next call restores it. */
export function persistBgSelection(sel: PersistedBg): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (sel.kind === 'none') {
      localStorage.removeItem(LS_KEY);
      return;
    }
    localStorage.setItem(LS_KEY, JSON.stringify(sel));
  } catch {
    /* storage full / disabled — selection just won't persist */
  }
}

/** Resolve any image id (preset or custom) to a usable URL. */
export function imageUrlForId(id: string | null): string | null {
  if (id === null) return null;
  return id.startsWith('custom:') ? customBackgroundUrl(id) : presetImageUrl(id);
}

/** Load the persisted selection (and resolve its image URL), or null. */
export function loadBgSelection(): { sel: PersistedBg; imageUrl: string | null } | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw === null) return null;
    const sel = JSON.parse(raw) as PersistedBg;
    if (sel.kind !== 'image') return { sel, imageUrl: null };
    return { sel, imageUrl: imageUrlForId(sel.imageId) };
  } catch {
    return null;
  }
}
